/**
 * Galería de fotos (página Egresados).
 *
 * - Splide en el preview (4 en 4 en desktop).
 * - Botón [data-open-fotos-modal] abre el modal galería.
 * - Modal lista de a 10 con paginador.
 * - Click en [data-foto-src] abre el lightbox.
 */
export function marianistaInitEgFotosGallery() {
  const galleryModal = /** @type {HTMLElement|null} */ (document.getElementById('eg-fotos-modal'));
  const lightbox = /** @type {HTMLElement|null} */ (document.getElementById('eg-lightbox'));

  initEgFotosSplide();

  if (!galleryModal && !lightbox) return;

  /* ── Galería modal + paginación ── */
  const galleryBackdrop = document.getElementById('eg-fotos-modal-backdrop');
  const galleryClose = document.getElementById('eg-fotos-modal-close');
  const grid = /** @type {HTMLElement|null} */ (document.getElementById('eg-fotos-modal-grid'));
  const pager = document.getElementById('eg-fotos-modal-pager');
  const pagerPrev = document.getElementById('eg-fotos-modal-prev');
  const pagerNext = document.getElementById('eg-fotos-modal-next');
  const pagerStatus = document.getElementById('eg-fotos-modal-status');

  const perPage = Math.max(1, Number(grid?.dataset.perPage || 10));
  const galleryItems = grid ? [...grid.querySelectorAll('.eg-fotos__item')] : [];
  const totalPages = Math.max(1, Math.ceil(galleryItems.length / perPage));
  let galleryPage = 1;

  const renderGalleryPage = (page) => {
    galleryPage = Math.min(Math.max(1, page), totalPages);
    const start = (galleryPage - 1) * perPage;
    const end = start + perPage;

    galleryItems.forEach((el, i) => {
      el.hidden = i < start || i >= end;
    });

    if (pager) {
      const showPager = galleryItems.length > perPage;
      pager.hidden = !showPager;
      if (showPager) {
        if (pagerStatus) {
          pagerStatus.textContent = `${galleryPage} / ${totalPages}`;
        }
        if (pagerPrev instanceof HTMLButtonElement) {
          pagerPrev.disabled = galleryPage <= 1;
        }
        if (pagerNext instanceof HTMLButtonElement) {
          pagerNext.disabled = galleryPage >= totalPages;
        }
      }
    }
  };

  renderGalleryPage(1);

  const openGallery = () => {
    if (!galleryModal) return;
    renderGalleryPage(1);
    galleryModal.classList.add('is-open');
    galleryModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    galleryClose instanceof HTMLElement && window.setTimeout(() => galleryClose.focus(), 60);
  };

  const closeGallery = () => {
    if (!galleryModal) return;
    galleryModal.classList.remove('is-open');
    galleryModal.setAttribute('aria-hidden', 'true');
    if (!lightbox?.classList.contains('is-open')) {
      document.body.style.overflow = '';
    }
  };

  document.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('[data-open-fotos-modal]')) {
      openGallery();
    }
  });

  galleryBackdrop?.addEventListener('click', closeGallery);
  galleryClose?.addEventListener('click', closeGallery);
  pagerPrev?.addEventListener('click', () => renderGalleryPage(galleryPage - 1));
  pagerNext?.addEventListener('click', () => renderGalleryPage(galleryPage + 1));

  /* ── Lightbox ── */
  /** @type {{ src: string; alt: string }[]} */
  let photos = [];
  let current = 0;

  const lbImg = /** @type {HTMLImageElement|null} */ (document.getElementById('eg-lightbox-img'));
  const lbClose = document.getElementById('eg-lightbox-close');
  const lbBackdrop = document.getElementById('eg-lightbox-backdrop');
  const lbPrev = document.getElementById('eg-lightbox-prev');
  const lbNext = document.getElementById('eg-lightbox-next');
  const lbCounter = document.getElementById('eg-lightbox-counter');

  const updateNav = () => {
    if (lbPrev) {
      lbPrev.toggleAttribute('data-hidden', current === 0);
    }
    if (lbNext) {
      lbNext.toggleAttribute('data-hidden', current === photos.length - 1);
    }
    if (lbCounter) {
      lbCounter.textContent = photos.length > 1 ? `${current + 1} / ${photos.length}` : '';
    }
  };

  const showPhoto = (index) => {
    current = index;
    if (lbImg) {
      lbImg.src = photos[current].src;
      lbImg.alt = photos[current].alt;
    }
    updateNav();
  };

  const openLightbox = (photoList, index) => {
    if (!lightbox) return;
    photos = photoList;
    showPhoto(index);
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    lbClose instanceof HTMLElement && window.setTimeout(() => lbClose.focus(), 60);
  };

  const closeLightbox = () => {
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    if (!galleryModal?.classList.contains('is-open')) {
      document.body.style.overflow = '';
    }
  };

  /** Recoge todas las fotos del contenedor (incluye ocultas por paginación). */
  const collectPhotos = (figure) => {
    const container =
      figure.closest('.eg-fotos-modal__grid') ||
      figure.closest('.eg-fotos__splide') ||
      figure.closest('.eg-fotos__mosaico');
    if (!container) return [];
    return [...container.querySelectorAll('[data-foto-src]')].map((el) => ({
      src: /** @type {string} */ (el instanceof HTMLElement ? el.dataset.fotoSrc : ''),
      alt: /** @type {string} */ (el instanceof HTMLElement ? el.dataset.fotoAlt || '' : ''),
    }));
  };

  const photoContainer = (figure) =>
    figure.closest('.eg-fotos-modal__grid') ||
    figure.closest('.eg-fotos__splide') ||
    figure.closest('.eg-fotos__mosaico') ||
    document;

  document.addEventListener('click', (e) => {
    const figure = e.target instanceof Element ? e.target.closest('[data-foto-src]') : null;
    if (!figure) return;
    const list = collectPhotos(figure);
    const index = [...photoContainer(figure).querySelectorAll('[data-foto-src]')].indexOf(figure);
    openLightbox(list, Math.max(0, index));
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const figure = e.target instanceof Element ? e.target.closest('[data-foto-src]') : null;
    if (!figure) return;
    e.preventDefault();
    figure.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  });

  lbClose?.addEventListener('click', closeLightbox);
  lbBackdrop?.addEventListener('click', closeLightbox);

  lbPrev?.addEventListener('click', () => {
    if (current > 0) showPhoto(current - 1);
  });

  lbNext?.addEventListener('click', () => {
    if (current < photos.length - 1) showPhoto(current + 1);
  });

  document.addEventListener('keydown', (e) => {
    if (lightbox?.classList.contains('is-open')) {
      if (e.key === 'Escape') {
        closeLightbox();
        return;
      }
      if (e.key === 'ArrowLeft' && current > 0) showPhoto(current - 1);
      if (e.key === 'ArrowRight' && current < photos.length - 1) showPhoto(current + 1);
    } else if (galleryModal?.classList.contains('is-open') && e.key === 'Escape') {
      closeGallery();
    }
  });
}

function initEgFotosSplide() {
  if (typeof Splide === 'undefined') {
    return;
  }

  document.querySelectorAll('.eg-fotos__splide').forEach((el) => {
    if (!(el instanceof HTMLElement) || el.dataset.egFotosMounted === '1') {
      return;
    }
    el.dataset.egFotosMounted = '1';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const slideCount = el.querySelectorAll('.splide__slide').length;
    const multiPage = slideCount > 1;

    new Splide(el, {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      gap: 0,
      pagination: multiPage,
      arrows: multiPage,
      drag: multiPage,
      speed: reducedMotion ? 0 : 450,
      easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    }).mount();
  });
}
